import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Flex,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  message,
} from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";

import { useTableAutoHeight } from "@/hooks/useTableAutoHeight";

import {
  createNotificationTemplateVariable,
  deleteNotificationTemplateVariable,
  getNotificationTemplateVariables,
  getNotificationVariableSchema,
  updateNotificationTemplateVariable,
} from "@/api/notices";

const codeCellStyle = {
  fontFamily:
    "ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace",
};

const VariablesTab = () => {
  const [pending, setPending] = useState(true);
  const [search, setSearch] = useState("");
  const [variables, setVariables] = useState([]);
  const [schema, setSchema] = useState([]);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorPending, setEditorPending] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const selectedObject = Form.useWatch("object_key", form);

  const { tableContainerRef, tableHeight, headerHeight } = useTableAutoHeight([
    variables,
    search,
  ]);

  const fetchVariables = useCallback(async () => {
    setPending(true);
    try {
      const list = await getNotificationTemplateVariables();
      setVariables(Array.isArray(list) ? list : []);
    } catch (error) {
      message.error(error.message);
    } finally {
      setPending(false);
    }
  }, []);

  useEffect(() => {
    fetchVariables();
  }, [fetchVariables]);

  useEffect(() => {
    (async () => {
      try {
        const objects = await getNotificationVariableSchema();
        setSchema(Array.isArray(objects) ? objects : []);
      } catch (error) {
        message.error(error.message);
      }
    })();
  }, []);

  const objectOptions = useMemo(
    () =>
      schema.map((o) => ({
        label: `${o.object_label} (${o.object_key})`,
        value: o.object_key,
      })),
    [schema]
  );

  // Атрибуты выбранного объекта, исключая уже существующие в каталоге пары.
  const attributeOptions = useMemo(() => {
    const obj = schema.find((o) => o.object_key === selectedObject);
    if (!obj) return [];
    const existing = new Set(variables.map((v) => v.unified_name));
    return obj.attributes.map((a) => ({
      label: `${a.attribute_key} — ${a.description}`,
      value: a.attribute_key,
      disabled: existing.has(a.unified_name),
    }));
  }, [schema, selectedObject, variables]);

  const openCreate = () => {
    setEditing(null);
    const initial = { object_key: undefined, attribute_key: undefined, description: "" };
    form.setFieldsValue(initial);
    setEditorOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({
      object_key: row.object_key,
      attribute_key: row.attribute_key,
      description: row.description || "",
    });
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditing(null);
    form.resetFields();
  };

  // При выборе атрибута подставляем его описание из схемы (если поле пустое).
  const onAttributeChange = (attribute_key) => {
    const obj = schema.find((o) => o.object_key === form.getFieldValue("object_key"));
    const attr = obj?.attributes.find((a) => a.attribute_key === attribute_key);
    if (attr && !form.getFieldValue("description")) {
      form.setFieldValue("description", attr.description || "");
    }
  };

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (e) {
      return;
    }
    setEditorPending(true);
    try {
      if (editing) {
        await updateNotificationTemplateVariable(editing.uuid, {
          description: values.description,
          updated_at: editing.updated_at,
        });
        message.success("Переменная обновлена");
      } else {
        await createNotificationTemplateVariable({
          object_key: values.object_key,
          attribute_key: values.attribute_key,
          description: values.description,
        });
        message.success("Переменная создана");
      }
      closeEditor();
      fetchVariables();
    } catch (error) {
      message.error(error.message);
    } finally {
      setEditorPending(false);
    }
  };

  const handleDelete = async (row) => {
    try {
      await deleteNotificationTemplateVariable(row.uuid, row.updated_at);
      message.success("Переменная удалена");
      fetchVariables();
    } catch (error) {
      message.error(error.message);
    }
  };

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return variables;
    return variables.filter(
      (v) =>
        (v.unified_name || "").toLowerCase().includes(needle) ||
        (v.description || "").toLowerCase().includes(needle)
    );
  }, [search, variables]);

  // Уникальные объекты для колоночного фильтра «Объект».
  const objectFilters = useMemo(() => {
    const byKey = new Map();
    for (const v of variables) {
      if (!v.object_key) continue;
      if (!byKey.has(v.object_key)) {
        byKey.set(v.object_key, v.object_label || v.object_key);
      }
    }
    return [...byKey.entries()]
      .map(([value, text]) => ({ text, value }))
      .sort((a, b) => a.text.localeCompare(b.text));
  }, [variables]);

  const columns = useMemo(
    () => [
      {
        title: "Переменная",
        dataIndex: "unified_name",
        key: "unified_name",
        width: 280,
        sorter: (a, b) =>
          (a.unified_name || "").localeCompare(b.unified_name || ""),
        render: (value, row) => (
          <Space>
            <span style={codeCellStyle}>[{value}]</span>
            {row.is_system && <Tag color="default">системная</Tag>}
          </Space>
        ),
      },
      {
        title: "Объект",
        dataIndex: "object_label",
        key: "object_label",
        width: 160,
        filters: objectFilters,
        onFilter: (value, row) => row.object_key === value,
        sorter: (a, b) =>
          (a.object_label || "").localeCompare(b.object_label || ""),
      },
      {
        title: "Атрибут",
        dataIndex: "attribute_key",
        key: "attribute_key",
        width: 180,
        sorter: (a, b) =>
          (a.attribute_key || "").localeCompare(b.attribute_key || ""),
        render: (value) => <span style={codeCellStyle}>{value}</span>,
      },
      {
        title: "Описание",
        dataIndex: "description",
        key: "description",
      },
      {
        title: "",
        key: "actions",
        width: 100,
        align: "center",
        render: (_, row) => (
          <Space>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(row)}
            />
            {!row.is_system && (
              <Popconfirm
                title="Удалить переменную?"
                description="Действие будет залогировано в audit_logs."
                okText="Удалить"
                okButtonProps={{ danger: true }}
                cancelText="Отмена"
                onConfirm={() => handleDelete(row)}
              >
                <Button type="text" size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            )}
          </Space>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [objectFilters]
  );

  return (
    <Flex vertical gap={16} className="h-100">
      <Flex gap={12} align="center" wrap="wrap">
        <Input.Search
          placeholder="Поиск переменной по имени или описанию"
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 480 }}
        />
        <Button type="primary" onClick={openCreate}>
          Создать переменную
        </Button>
      </Flex>

      <div ref={tableContainerRef} style={{ flex: 1, minHeight: 0 }}>
        <Table
          rowKey="uuid"
          columns={columns}
          dataSource={filtered}
          loading={pending}
          pagination={false}
          scroll={{ x: "100%", y: tableHeight - headerHeight }}
        />
      </div>

      <Modal
        title={editing ? "Редактирование переменной" : "Создание переменной"}
        open={editorOpen}
        onCancel={closeEditor}
        onOk={handleSubmit}
        okText={editing ? "Сохранить" : "Создать"}
        cancelText="Отмена"
        confirmLoading={editorPending}
        width={560}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Объект"
            name="object_key"
            rules={[{ required: true, message: "Выберите объект" }]}
          >
            <Select
              options={objectOptions}
              disabled={Boolean(editing)}
              placeholder="Выберите объект"
              onChange={() => form.setFieldValue("attribute_key", undefined)}
            />
          </Form.Item>
          <Form.Item
            label="Атрибут"
            name="attribute_key"
            rules={[{ required: true, message: "Выберите атрибут" }]}
          >
            <Select
              options={attributeOptions}
              disabled={Boolean(editing) || !selectedObject}
              placeholder="Выберите атрибут"
              onChange={onAttributeChange}
            />
          </Form.Item>
          <Form.Item label="Описание" name="description">
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
          </Form.Item>
        </Form>
      </Modal>
    </Flex>
  );
};

export default VariablesTab;
